import { supabase } from "@/integrations/supabase/client";
import { resolveVoiceId, type ProtagonistVoiceInput } from "@/services/protagonistVoices";

/**
 * The protagonist's ElevenLabs voice, read from the catalogue.
 *
 * `transmissions.elevenlabs_agent_id` is the single source of truth for which
 * voice a character speaks in, and it is the one both clients share. Despite the
 * column name it holds an ElevenLabs **voice** id, which is what the TTS
 * override and `elevenlabs-tts` both expect.
 *
 * WHY READ IT INSTEAD OF DERIVING IT. The website used to compute the voice from
 * the character's name and an archetype guess. Measured across all 147 rows,
 * that derivation was wrong for 44 of them, while the column was wrong for 0.
 * The column is also richer: it carries bespoke Spotlight voices that exist
 * nowhere in the local palette — Paul Atreides, Arthur Dent, Winston Smith,
 * Hari Seldon, Ariane Emory, Mona Lisa, A. Square, Bobby Newmark and Commander
 * Norton each have their own cut. Deriving could never produce those.
 *
 * The iOS app made the same move (VoiceConfig no longer derives), so reading the
 * column is what keeps the two clients in step — not porting logic between them.
 *
 * `resolveVoiceId` stays as the fallback for a row that has no value yet, and
 * for callers with no id. It is deliberately kept pure and dependency-free so it
 * can be exercised on its own.
 */
export async function resolveVoiceIdForProtagonist(
  protagonistId: number | null | undefined,
  fallback: ProtagonistVoiceInput,
): Promise<string> {
  if (protagonistId != null) {
    try {
      const { data, error } = await supabase
        .from("transmissions")
        .select("elevenlabs_agent_id")
        .eq("id", protagonistId)
        .maybeSingle();

      if (error) {
        console.warn("[voice] catalogue lookup failed, using local fallback:", error.message);
      } else if (data?.elevenlabs_agent_id) {
        return data.elevenlabs_agent_id;
      } else {
        console.warn(`[voice] transmissions.${protagonistId} has no elevenlabs_agent_id; using local fallback`);
      }
    } catch (err) {
      console.warn("[voice] catalogue lookup threw, using local fallback:", err);
    }
  }
  return resolveVoiceId(fallback);
}
